import { Button } from 'components/atoms';
import { FormInput } from 'components/molecules';
import { PublicPage } from 'components/templates';
import { passwordRegExp } from 'constants/passwordRegExp';
import { Form, Formik, FormikHelpers } from 'formik';
import { useMutateOptions } from 'hooks';
import i18next from 'i18next';
import { Trans, useTranslation } from 'react-i18next';
import {
  PiCaretLeft,
  PiConfetti,
  PiFingerprintSimple,
  PiUserCircle,
} from 'react-icons/pi';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useConfirmEmail } from 'services/auth/confirmEmail';
import { IConfirmEmailForm } from 'services/auth/types';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { object, ref, string } from 'yup';

const confirmEmailValidationSchema = () =>
  object().shape({
    password: string()
      .matches(passwordRegExp, i18next.t('formik-error.invalid-password'))
      .required(i18next.t('formik-error.required')),
    passwordCopy: string()
      .oneOf([ref('password'), ''], i18next.t('formik-error.unmatch-password'))
      .required(i18next.t('formik-error.required')),
  });

const ConfirmEmail = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const confirmEmail = useConfirmEmail();

  const mutateOptions = useMutateOptions<IConfirmEmailForm>();

  const email = params.get('email') ?? '';
  const token = params.get('token')?.replaceAll(' ', '+') ?? '';

  const goToLogin = () => navigate(`/login?email=${email}`);

  const onConfirmEmail = async (
    values: IConfirmEmailForm,
    helpers: FormikHelpers<IConfirmEmailForm>
  ) => {
    await confirmEmail.mutateAsync(values, {
      ...mutateOptions(helpers),
      onSuccess: goToLogin,
    });
  };

  return (
    <PublicPage
      header={
        <>
          <PiUserCircle />
          {t('confirm-email.header')}
        </>
      }
      creative={
        <div
          className='flex flex-col h-full w-96 gap-4 p-30 items-center '
          style={{
            backgroundColor: 'var(--secondary-soft)',
            color: 'var(--primary)',
          }}>
          <div className='flex gap-4 mt-16 mb-10  items-center'>
            <PiConfetti size={50} />
            <p className='text-lg font-semibold'>
              {t('confirm-email.creative.header')}
            </p>
          </div>
          <p>
            <Trans
              i18nKey='confirm-email.creative.welcome'
              values={{ email: email }}
              components={{ strong: <strong /> }}
            />
          </p>
          <p className='text-center'>{t('confirm-email.creative.message')}</p>
        </div>
      }
      footer={
        <div
          className='flex gap-2 cursor-pointer p-4 items-center'
          onClick={goToLogin}>
          <PiCaretLeft />
          {t('confirm-email.back')}
        </div>
      }>
      <div className='w-72 opacity-65'>
        {t('reset-password.password-reg-exp')}
      </div>
      <Formik
        validateOnBlur
        validationSchema={confirmEmailValidationSchema}
        initialValues={{ email, token, password: '', passwordCopy: '' }}
        enableReinitialize
        onSubmit={onConfirmEmail}>
        <Form>
          <FormInput.Text
            name='password'
            type='password'
            label={t('input.label.password')}
          />
          <FormInput.Text
            name='passwordCopy'
            type='password'
            label={t('input.label.confirm-password')}
          />
          <Button
            type='submit'
            variant={BUTTON_VARIANT.submit}
            isLoading={confirmEmail.isPending}
            fullWidth>
            <Button.Text text={t('confirm-email.button')} />
            <Button.Icon icon={<PiFingerprintSimple />} />
          </Button>
        </Form>
      </Formik>
    </PublicPage>
  );
};

export default ConfirmEmail;
